// Shared emoji/text segmenter used by the AOTD-IRL layout editor to mirror
// the server's inline-emoji rendering inside the live SVG preview.

export type EmojiSegment = { kind: "text"; s: string } | { kind: "emoji"; s: string; cp: string };

const PICTO_RE = /\p{Extended_Pictographic}/u;

interface GraphemeSegmenter {
  segment(input: string): Iterable<{ segment: string }>;
}
const IntlAny = Intl as unknown as {
  Segmenter?: new (locale: string, options: { granularity: "grapheme" }) => GraphemeSegmenter;
};

let segmenter: GraphemeSegmenter | null = null;
function getSegmenter(): GraphemeSegmenter {
  if (segmenter) return segmenter;
  if (IntlAny.Segmenter) {
    segmenter = new IntlAny.Segmenter("en", { granularity: "grapheme" });
    return segmenter;
  }
  // Fallback: treat each unit as its own grapheme (no ZWJ awareness).
  segmenter = {
    segment(input: string) {
      return Array.from(input).map((s) => ({ segment: s }));
    },
  };
  return segmenter;
}

/** Convert an emoji grapheme to its codepoint filename (e.g. "1f60a"). */
export function emojiToCodepoint(emoji: string): string {
  const codepoints: number[] = [];
  for (const ch of emoji) {
    const cp = ch.codePointAt(0);
    if (cp !== undefined) codepoints.push(cp);
  }
  const filtered = codepoints.filter((cp) => cp !== 0xfe0e && cp !== 0xfe0f);
  return filtered.map((cp) => cp.toString(16)).join("-");
}

export function segmentEmojis(input: string): EmojiSegment[] {
  if (!input) return [];
  const out: EmojiSegment[] = [];
  let buf = "";
  for (const { segment } of getSegmenter().segment(input)) {
    if (PICTO_RE.test(segment)) {
      if (buf) {
        out.push({ kind: "text", s: buf });
        buf = "";
      }
      out.push({ kind: "emoji", s: segment, cp: emojiToCodepoint(segment) });
    } else {
      buf += segment;
    }
  }
  if (buf) out.push({ kind: "text", s: buf });
  return out;
}
